const GRID_FILTER_TEXT_PARENT_TYPES = new Set(['filterRow', 'searchPanel']);

interface TrimmedGridFilterTextEditorPreparing {
  readonly parentType?: string;
  editorName?: string;
  editorOptions?: {
    onValueChanged?: (args: GridFilterTextValueChangedArgs) => void;
    onPaste?: (args: unknown) => void;
  } | null;
}

interface GridFilterTextValueChangedArgs {
  value?: unknown;
  event?: unknown;
  component?: {
    option(optionName: string, value?: string): unknown;
  };
}

interface FilterInputEvent {
  type?: string;
  key?: string;
  inputType?: string;
  originalEvent?: FilterInputEvent;
}

/**
 * Filter-row and search text is normalized before it is shown or applied.
 * Non-breaking and other Unicode spaces become a normal space: Chrome draws
 * U+00A0 as a trailing dot. Zero-width characters are removed, repeated spaces
 * collapse, and spaces at both ends are removed. A trailing period is removed,
 * including the period macOS inserts after two spaces. One ordinary trailing
 * space stays while the next word is typed.
 */
export function attachTrimmedGridFilterTextEditor(event: TrimmedGridFilterTextEditorPreparing): void {
  if (event.editorName !== 'dxTextBox') {
    return;
  }
  if (!event.parentType || !GRID_FILTER_TEXT_PARENT_TYPES.has(event.parentType)) {
    return;
  }

  const editorOptions = event.editorOptions ?? {};
  event.editorOptions = editorOptions;
  const previousOnValueChanged = editorOptions.onValueChanged;
  const previousOnPaste = editorOptions.onPaste;
  let trimTrailingOnNextChange = false;
  let isApplyingValue = false;

  editorOptions.onPaste = (pasteArgs: unknown): void => {
    trimTrailingOnNextChange = true;
    callEditorHandler(previousOnPaste, pasteArgs);
  };

  editorOptions.onValueChanged = (args: GridFilterTextValueChangedArgs): void => {
    const pastedText = trimTrailingOnNextChange;
    trimTrailingOnNextChange = false;

    if (isApplyingValue && isTrimmedFilterText(args?.value)) {
      return;
    }
    if (isProgrammaticTrimmedValue(args)) {
      return;
    }

    const rawValue = args?.value;
    if (typeof rawValue !== 'string') {
      callPreviousValueChanged(previousOnValueChanged, args);
      return;
    }

    const displayValue = normalizeGridFilterText(rawValue, pastedText || shouldTrimTrailingGridFilterText(args.event));
    if (displayValue !== rawValue && args.component) {
      args.component.option('value', displayValue);
      return;
    }

    const committedValue = normalizeGridFilterText(rawValue, true);
    if (committedValue !== rawValue) {
      args.value = committedValue;
    }

    isApplyingValue = true;
    try {
      callPreviousValueChanged(previousOnValueChanged, args);
    } finally {
      isApplyingValue = false;
    }
  };
}

function isTrimmedFilterText(value: unknown): boolean {
  if (typeof value !== 'string') {
    return true;
  }

  return normalizeGridFilterText(value, true) === value;
}

function isProgrammaticTrimmedValue(args: GridFilterTextValueChangedArgs | undefined): boolean {
  if (!args || args.event) {
    return false;
  }

  return isTrimmedFilterText(args.value);
}

const UNICODE_SPACE_PATTERN = /[\u00A0\u1680\u2000-\u200A\u202F\u205F\u3000]/g;
const ZERO_WIDTH_PATTERN = /[\u200B-\u200D\uFEFF]/g;
const TRAILING_SPECIAL_SPACE_PATTERN = /[\u00A0\u1680\u2000-\u200A\u202F\u205F\u3000\u200B-\u200D\uFEFF]\s*$/;
const TRAILING_DOT_PATTERN = /\s*\.[\s.]*$/;

/**
 * Turns Unicode spaces into a normal space, drops zero-width characters, and
 * collapses repeated spaces and tabs. Leading spaces are always removed.
 * A trailing period is removed, including spaces around it. macOS turns two
 * spaces into a period.
 * Periods inside the text stay. Trailing spaces are removed when
 * `trimTrailing` is set, and also when the value ends with a non-breaking or
 * other special space — Chrome draws U+00A0 as a dot. Otherwise one ordinary
 * trailing space is kept so the next word can be typed.
 */
function normalizeGridFilterText(value: string, trimTrailing: boolean): string {
  const normalized = value
    .replace(UNICODE_SPACE_PATTERN, ' ')
    .replace(ZERO_WIDTH_PATTERN, '')
    .replace(/[ \t]+/g, ' ')
    .replace(/^\s+/, '')
    .replace(TRAILING_DOT_PATTERN, '');
  const trimEnds = trimTrailing || TRAILING_SPECIAL_SPACE_PATTERN.test(value);
  if (trimEnds) {
    return normalized.trim();
  }

  return normalized;
}

function shouldTrimTrailingGridFilterText(event: unknown): boolean {
  const inputEvent = readFilterInputEvent(event);
  if (!inputEvent) {
    return false;
  }

  const nativeEvent = inputEvent.originalEvent ?? inputEvent;
  const inputType = nativeEvent.inputType;
  if (inputType === 'insertFromPaste' || inputType === 'insertFromDrop') {
    return true;
  }

  const eventType = inputEvent.type ?? nativeEvent.type;
  if (eventType === 'change' || eventType === 'search') {
    return true;
  }

  const key = inputEvent.key ?? nativeEvent.key;
  return key === 'Enter';
}

function readFilterInputEvent(event: unknown): FilterInputEvent | undefined {
  if (!event || typeof event !== 'object') {
    return undefined;
  }

  return event as FilterInputEvent;
}

function callPreviousValueChanged(previousOnValueChanged: unknown, args: GridFilterTextValueChangedArgs): void {
  callEditorHandler(previousOnValueChanged, args);
}

function callEditorHandler(handler: unknown, args: unknown): void {
  if (typeof handler !== 'function') {
    return;
  }

  (handler as (handlerArgs: unknown) => void)(args);
}
